import React, { useMemo, useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';

import { useLanguage, Language } from '../../context/LanguageContext';
import { useRegion } from '../../context/RegionContext';
import { JewelleryCollectionId } from '../../data/jewellery';
import { getJewelleryItemView } from '../../utils/jewelleryItemLoader';
import { JewelleryCollectionItemView } from '../../utils/jewelleryCollectionUtils';
import { PricingService, JewelleryPricingResponse } from '../../services/pricingService';

const VALID_COLLECTIONS: JewelleryCollectionId[] = ['tourmaline', 'snake', 'fluoring', 'chinese_zodiacs'];

function isValidCollectionId(value: string | undefined): value is JewelleryCollectionId {
    return !!value && (VALID_COLLECTIONS as string[]).includes(value);
}

interface JewelleryDetailTranslations {
    nameLabel: string;
    descriptionLabel: string;
    priceLabel: string;
    priceOnRequest: string;
    itemNotFound: string;
    requestedItemNotFound: string;
    unableToLoad: string;
    backToHome: string;
    backToCollection: string;
    certificationNote: string;
}

const jewelleryDetailTranslations: Record<Language, JewelleryDetailTranslations> = {
    en: {
        nameLabel: 'Name',
        descriptionLabel: 'Description',
        priceLabel: 'Price',
        priceOnRequest: 'Price on request',
        itemNotFound: 'Jewellery item not found.',
        requestedItemNotFound: 'The requested jewellery item could not be found.',
        unableToLoad: 'Unable to load jewellery item.',
        backToHome: 'Back to home',
        backToCollection: 'Back to collection',
        certificationNote: 'Certification of frequency charges extra, approx. €390'
    },
    de: {
        nameLabel: 'Name',
        descriptionLabel: 'Beschreibung',
        priceLabel: 'Preis',
        priceOnRequest: 'Preis auf Anfrage',
        itemNotFound: 'Schmuckstück nicht gefunden.',
        requestedItemNotFound: 'Das angeforderte Schmuckstück konnte nicht gefunden werden.',
        unableToLoad: 'Schmuckstück konnte nicht geladen werden.',
        backToHome: 'Zurück zur Startseite',
        backToCollection: 'Zurück zur Kollektion',
        certificationNote: 'Frequenzzertifizierung kostet extra, ca. €390'
    },
    zh: {
        nameLabel: '名稱',
        descriptionLabel: '描述',
        priceLabel: '價格',
        priceOnRequest: '價格請詢問',
        itemNotFound: '找不到珠寶商品。',
        requestedItemNotFound: '無法找到所請求的珠寶商品。',
        unableToLoad: '無法載入珠寶商品。',
        backToHome: '返回首頁',
        backToCollection: '返回系列',
        certificationNote: '頻率認證需額外收費，約€390'
    },
    fr: {
        nameLabel: 'Nom',
        descriptionLabel: 'Description',
        priceLabel: 'Prix',
        priceOnRequest: 'Prix sur demande',
        itemNotFound: 'Bijou introuvable.',
        requestedItemNotFound: 'Le bijou demandé est introuvable.',
        unableToLoad: 'Impossible de charger le bijou.',
        backToHome: "Retour à l'accueil",
        backToCollection: 'Retour à la collection',
        certificationNote: 'Certification de fréquence en supplément, env. €390'
    }
};

function getJewelleryDetailTranslations(language: Language): JewelleryDetailTranslations {
    return jewelleryDetailTranslations[language] || jewelleryDetailTranslations.en;
}

function JewelleryItemDetail() {
    const params = useParams<{ collectionId?: string; itemId?: string }>();
    const { language } = useLanguage();
    const { region, formatPrice } = useRegion();
    const translations = useMemo(() => getJewelleryDetailTranslations(language), [language]);

    // Pricing state
    const [pricingLoading, setPricingLoading] = useState<boolean>(false);
    const [pricingError, setPricingError] = useState<boolean>(false);
    const [regionalPricing, setRegionalPricing] = useState<JewelleryPricingResponse | null>(null);

    const collectionId = useMemo(() => {
        if (isValidCollectionId(params.collectionId)) {
            return params.collectionId;
        }
        return undefined;
    }, [params.collectionId]);

    const itemId = params.itemId || '';

    const item: JewelleryCollectionItemView | null = useMemo(() => {
        if (!collectionId || !itemId) {
            return null;
        }
        const view = getJewelleryItemView(language, collectionId, itemId);
        return view || null;
    }, [collectionId, itemId, language]);

    // Fetch regional pricing when component mounts or region changes
    useEffect(() => {
        const fetchPricing = async () => {
            if (!collectionId || !itemId || !item) {
                return;
            }

            // Check if item has a valid price
            if (!item.price || item.price.trim() === '' || !PricingService.isPriceAvailable(item.price)) {
                return;
            }

            setPricingLoading(true);
            setPricingError(false);
            setRegionalPricing(null);

            try {
                const response = await PricingService.getJewelleryRegionalPricing(collectionId, itemId, region);
                setRegionalPricing(response);
            } catch (error) {
                console.error('Failed to fetch jewelry pricing:', error);
                setPricingError(true);
            } finally {
                setPricingLoading(false);
            }
        };

        fetchPricing();
    }, [collectionId, itemId, region, item]);

    if (!collectionId || !itemId) {
        return (
            <div className="flex min-h-screen flex-col items-center justify-center bg-white">
                <p className="text-gray-600">{translations.requestedItemNotFound}</p>
                <Link to="/" className="mt-4 text-sm text-blue-600 hover:underline">{translations.backToHome}</Link>
            </div>
        );
    }

    if (!item) {
        return (
            <div className="flex min-h-screen flex-col items-center justify-center bg-white">
                <p className="text-gray-600">{translations.unableToLoad}</p>
                <Link to={`/collections/${collectionId}`} className="mt-4 text-sm text-blue-600 hover:underline">
                    {translations.backToCollection}
                </Link>
            </div>
        );
    }

    const renderPrice = (): React.ReactNode => {
        if (!item) return translations.priceOnRequest;

        // Check if item has a valid price
        if (!item.price || item.price.trim() === '' || !PricingService.isPriceAvailable(item.price)) {
            return (
                <span className="text-2xl font-light text-gray-900">
                    {translations.priceOnRequest}
                </span>
            );
        }

        // Show loading state while pricing is being fetched
        if (pricingLoading) {
            return (
                <div className="flex items-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-gray-300 border-t-gray-600"></div>
                </div>
            );
        }

        // Use regional pricing if available
        if (regionalPricing && !pricingError) {
            return (
                <span className="text-2xl font-light text-gray-900">
                    {formatPrice(regionalPricing.regionalPricing.price)}
                </span>
            );
        }

        // If API failed, show "Price on request"
        if (pricingError) {
            return (
                <span className="text-2xl font-light text-gray-900">
                    {translations.priceOnRequest}
                </span>
            );
        }

        // Default fallback
        return (
            <span className="text-2xl font-light text-gray-900">
                {translations.priceOnRequest}
            </span>
        );
    };

    return (
        <div className="bg-white min-h-screen pt-24 md:pt-32">
            <div className="mx-auto mt-12 max-w-6xl px-4 pb-16">
                <div className="grid gap-12 md:grid-cols-2">
                    <div className="order-2 md:order-1">
                        <div className="overflow-hidden rounded-lg border border-gray-200 bg-gray-50">
                            <img
                                src={item.image}
                                alt={item.name}
                                className="h-full w-full object-contain"
                            />
                        </div>
                        {item.carouselImages && item.carouselImages.length > 0 && (
                            <div className="mt-6 grid grid-cols-3 gap-3">
                                {item.carouselImages.map((src) => (
                                    <div key={src} className="overflow-hidden rounded border border-gray-200 bg-white">
                                        <img src={src} alt={item.name} className="h-24 w-full object-cover" />
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="order-1 flex flex-col space-y-6 md:order-2">
                        <div className="space-y-1">
                            <span className="block text-xs tracking-widest text-gray-500 uppercase">{translations.nameLabel}</span>
                            <p className="text-base text-gray-800 whitespace-pre-line">{item.name}</p>
                        </div>
                        <div className="space-y-1">
                            <span className="block text-xs tracking-widest text-gray-500 uppercase">{translations.descriptionLabel}</span>
                            <p className="text-base text-gray-800 whitespace-pre-line">{item.description}</p>
                        </div>
                        <div>
                            <span className="block text-xs tracking-widest text-gray-500 uppercase">{translations.priceLabel}</span>
                            <div className="mt-1">{renderPrice()}</div>
                        </div>
                        {collectionId === 'tourmaline' && (
                            <div className="mt-4">
                                <p className="text-sm text-gray-600 italic">{translations.certificationNote}</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default JewelleryItemDetail;
